import { useState } from "react"
import { useForm } from "react-hook-form"
import { useVectorModal } from "../../../hooks/useVectorMemoryModal"
import { zodResolver } from "@hookform/resolvers/zod"
import z from "zod"

import {
	Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Checkbox,
    FormControl,
    IconButton,
    InputLabel,
    List,
    ListItem,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography
} from "@mui/material"
import { CaretDown, Check, PencilSimple, Plus, TrashSimple, X } from "@phosphor-icons/react"
import { VectorMetadataItem } from "./VectorMetadataItem"

import { selectChunkingOptions } from "."

import type { VectorFileData } from "./vectorModal"
import type { VectorChunkingMethod, VectorMetadata } from "../../../types/vector"

const metadataSchema = z.object({
	name: z
		.string()
		.min(1, { message: 'Name is required' }),
	value: z.string().min(1, { message: 'Value is required' })
})

interface UploadItemProps extends Omit<VectorFileData, 'fileUrl'> {
	checked: boolean
	onSelectedFilesChange: (fileId: string) => void
	onChunkingMethodChange?: (fileId: string, chunkingMethod: VectorChunkingMethod) => void
}

type MetadataData = z.infer<typeof metadataSchema>

function UploadItem({
	checked,
	chunking,
	customFileName,
	fileName,
	id,
	metadatas,
	onSelectedFilesChange,
	onChunkingMethodChange,
}: UploadItemProps) {
	const {
		stagingFiles,
		onStagingFilesChange,

		storedFiles,

		onVectorFileDelete,

		onMetadataCreate,
		onMetadataDelete,
	} = useVectorModal()

	const isStoredFile = storedFiles.find((storedFile) => storedFile.id === id)

	const {
		register,
		handleSubmit,
		formState: { errors },
		setError,
		reset,
	} = useForm({
		defaultValues: {
			name: '',
			value: ''
		},
		resolver: zodResolver(metadataSchema)
	})

	const [expanded, setExpanded] = useState(false)
	const [isEditingName, setIsEditingName] = useState(false)
	const [name, setName] = useState(customFileName ? customFileName : fileName)

	const handleExpandedToggle = () => {
		setExpanded((prev) => !prev)
	}

	const handleMetadataAdd = ({ name, value }: MetadataData) => {
		const nameFormatted = name.toLowerCase().split(' ').join('_')

		const hasDuplicateKeyName = metadatas.find((metadata) => metadata.name === nameFormatted)

		if (hasDuplicateKeyName !== undefined) {
			setError("name", { message: 'Label already exists'})
			return
		}

		const newMetadata: VectorMetadata = {
			name: nameFormatted,
			value
		}

		onMetadataCreate({ filesIds: [id], metadatas: [newMetadata] })
		reset()
	}

	const handleMetadataDelete = (metadataName: string) => {
		onMetadataDelete({ filesIds: [id], metadatas: [{ name: metadataName }] })
	}

	const handleNameChange = (newName: string) => {
		setName(newName)
	}

	const handleCustomNameApply = (customName: string) => {
		const newFileList = stagingFiles.map((file) => {
			if (file.id === id) {
				return {
					...file,
					customFileName: customName
				}
			}

			return file
		})

		onStagingFilesChange(newFileList)
		setIsEditingName(false)
	}

	const MAX_METADATAS = 2

	const hasMetadataOverflow = metadatas.length > MAX_METADATAS
	const metadataOverflowAmount = metadatas.length - MAX_METADATAS
	const visibleMetadatas = metadatas.slice(0, MAX_METADATAS)
	const hiddenMetadatas = metadatas.slice(MAX_METADATAS)

	const selectedChunking = selectChunkingOptions.find(({ value }) => value === chunking)

	return (
		<ListItem
			sx={{
				backgroundColor: expanded ? '#fafafa' : '#fff',
				borderRadius: '2rem'
			}}
			>
			<Accordion
				expanded={expanded}
				sx={{
					backgroundColor: 'transparent',

					width: '100%',
					height: 'auto !important',
					minWidth: 'auto !important',
					minHeight: 'auto !important',

					boxShadow: 'none',
					
					borderRadius: '0 !important',
					borderBlockEndColor: expanded ? 'transparent' : '#e4e4e7',
					borderBlockEndStyle: 'solid',
					borderBlockEndWidth: '1px',

					'&:hover [data-actions]': {
						display: 'block',
					}
				}}
				onChange={handleExpandedToggle}
			>
				<AccordionSummary
					expandIcon={<CaretDown size={20} />}
					sx={{
						minHeight: 'auto',

						'& > .MuiAccordionSummary-content': {
							position: 'relative',
							isolation: 'isolate',

							width: '100%',
							marginBlock: '0.75rem',
							marginInlineEnd: isEditingName ? '1.25rem' : '0',

							display: 'grid',
							gridTemplateColumns:
								isEditingName ? '1fr' : expanded ? 'min-content 1fr' : 'min-content 25ch max-content 1fr',
							alignItems: 'center',
							gap: '0.5rem',
						},

						'& > .MuiAccordionSummary-content.Mui-expanded': {
							marginBlock: '0.75rem'
						},

						'&:focus-within': {
							backgroundColor: 'transparent'
						}
					}}
				>
					{isEditingName ? (
						<Box
							sx={{
								width: '100%',
								position: 'relative',

								display: 'flex',
								alignItems: 'center',
								gap: '0.5rem',
							}}
						>
							<TextField
								fullWidth
								value={name}
								onClick={(evt) => evt.stopPropagation()}
								onChange={(evt) => handleNameChange(evt.target.value)}
							/> 

							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: '0.5rem'
								}}
							>
								<IconButton
									size="small"
									color="success"
									aria-label="Apply custom name"
									onClick={(evt) => {
										evt.stopPropagation()
										handleCustomNameApply(name)
									}}
								>
									<Check />
								</IconButton>

								<IconButton
									size="small"
									color="error"
									aria-label="Cancel edit custom name"
									onClick={(evt) => {
										evt.stopPropagation()
										setIsEditingName(false)
									}}
								>
									<X />
								</IconButton>
							</Box>
						</Box>
					) : (
						<>
							<Checkbox
								sx={{ padding: 0 }}
								checked={checked}
								onClick={(evt) => evt.stopPropagation()}
								onChange={() => onSelectedFilesChange(id)}
							/>

							<Tooltip title={name}>
								<Typography
									sx={{
										maxWidth: expanded ? 'auto' : '25ch',
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
									}}
									color="#000"
									fontWeight={600}
								>
									{name}
								</Typography>
							</Tooltip>

							{!expanded ? (
								<Typography
									sx={{
										backgroundColor: '#f4f4f5',
										paddingBlock: '0.25rem',
										paddingInline: '0.5rem',
										borderRadius: '9999vmax',

										fontSize: '0.825rem',
									}}
								>
									Chunked by {selectedChunking ? selectedChunking.label : null}
								</Typography>
							) : null}

							{!expanded ? (
								<List sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
									{visibleMetadatas.map(({ name, value }) => (
										<VectorMetadataItem key={name} name={name} value={value} />
									))}

									{hasMetadataOverflow ? (
										<Tooltip title={
											<>
												{hiddenMetadatas.map(({ name, value }) => (
													<Typography key={name}>
														{name}:{value}
													</Typography>
												))}
											</>
										}>
											<Typography
												component="span"
												sx={{ display: 'inline-block', fontSize: '0.825rem' }}
											>
												+ {metadataOverflowAmount} more
											</Typography>
										</Tooltip>
									) : null}
								</List>
							) : null}

							<Box
								data-actions
								sx={{
									display: 'none',

									position: 'absolute',
									right: '0',

									paddingInline: '0.5rem',
								}}
							>
								<Button
									disableRipple
									disabled={!!isStoredFile}
									sx={{
										minWidth: 'auto',
										paddingBlock: '0.25rem',
										paddingInline: '0.5rem',

										borderRadius: '0.5rem'
									}}
									onClick={(evt) => {
										evt.stopPropagation()
										setIsEditingName(true)
									}}
								>
									<PencilSimple />
								</Button>

								<Button
									disableRipple
									sx={{
										minWidth: 'auto',
										paddingBlock: '0.25rem',
										paddingInline: '0.5rem',

										borderRadius: '0.5rem',

										'&:hover': {
											backgroundColor: '#fef2f2'
										}
									}}
									onClick={(evt) => {
										evt.stopPropagation()
										onVectorFileDelete(id)
									}}
								>
									<TrashSimple color="#dc2626" />
								</Button>
							</Box>
						</>
					)}
				</AccordionSummary>

				<AccordionDetails sx={{ '&>*+*': { marginBlockStart: '1.25rem !important' }}}>
					<FormControl 
						fullWidth
						variant="outlined"
						size="medium"
					>
						<InputLabel 
							id="chunking"
							sx={{ color: "#9e9e9e" }}
						>
							Chunking
						</InputLabel>

						<Select
							id="chunking-select"
							fullWidth
							sx={{
								marginBottom: 0,
							}}
							label="Chunking"
							labelId="chunking"
							value={chunking}
							onChange={(evt) =>
								onChunkingMethodChange?.( id, evt.target.value as VectorChunkingMethod)
							}
							disabled={!!isStoredFile}
						>
							{selectChunkingOptions.map(({ label, value }) => (
								<MenuItem key={value} value={value}>
									Chunk by {label}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<List
						sx={{
							padding: 0,
							display: 'flex',
							flexWrap: 'wrap',
							gap: '1rem'
						}}
					>
						{metadatas.map(({ name, value }) => (
							<VectorMetadataItem
								key={name}
								name={name}
								value={value}
								onMetadataDelete={handleMetadataDelete}
							/>
						))}

					</List>

					<Box
						component="form"
						id={`add-new-label-${id}`}
						sx={{ '&>*+*': { marginBlockStart: '0.25rem !important' }}}
						onSubmit={handleSubmit(handleMetadataAdd)}
					>
						<Typography
							variant="h4"
							fontSize="1rem"
							color="black"
							fontWeight={500}
						>
							Add New Label
						</Typography>

						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '1rem'
							}}
						>
							<TextField
								placeholder="Type Key here"
								sx={{ flex: 1 }}
								error={!!errors.name}
								helperText={errors.name?.message}
								{...register('name')}
							/>

							<TextField
								placeholder="Type Value here"
								sx={{ flex: 1 }}
								error={!!errors.value}
								helperText={errors.value?.message}
								{...register('value')}
							/>

							<Button
								type="submit"
								variant="outlined"
								form={`add-new-label-${id}`}
							>
								Add
								<Plus size={16} />
							</Button>
						</Box>
					</Box>
				</AccordionDetails>
			</Accordion>
		</ListItem>
	)
}

export { UploadItem, metadataSchema }